import type { GameWorld } from '../game/world'
import type { Camera } from '../engine/camera'
import type { Entity, PlayerForm } from '../game/components'
import type { BiomeId } from '../game/biomes'
import type { RenderSystem, ExtraPlayerSpec } from '../game/systems/render'
import { attachEyes } from '../game/systems/eyes'
import { packRecipe, type CreatureRecipe } from '../game/creature-recipe'
import { PLAYER_FORMS, FORM_DISPLAY } from '../game/systems/lineage'
import { growthAt } from '../game/evolution'
import type { Tier } from '../game/evolution'

const GALLERY_X = 100
const GALLERY_Y = 100
const COL_SPACING = 0.55
const ROW_SPACING = 0.85

// Centering: all rows use col 0–4 centered at col 2.0.
// 4-item rows use fractional cols (0.5, 1.5, 2.5, 3.5) to stay centered.
function worldPos(row: number, colF: number): { x: number; y: number } {
  return {
    x: GALLERY_X + (colF - 2.0) * COL_SPACING,
    y: GALLERY_Y + (1.0 - row) * ROW_SPACING,
  }
}

interface CreatureSpec {
  label: string
  row: number
  colF: number
  // Authored as a recipe; packed to morphology floats at build (single source
  // of truth with the live game — see creature-recipe.ts).
  recipe: CreatureRecipe
  radius: number
  tint: [number, number, number]
  variant: BiomeId
  wobbleFreq: number
  isChainSegment?: true
  // Eyes rule (visual direction): agents get eyes in the gallery too, so the
  // lineup is sanity-checked with faces included.
  eyes?: 'angry' | 'chibi'
}

interface MutationSpec {
  label: string
  colF: number
  mutationFlags: number
  mutationStacks: number
  tier?: number
}

// Row 0 — microbes (4 items, fractional cols so they're centered like the 5-item rows)
// Row 1 — predators + allies + chain boss (5 items)
// Row 3 — new bodies + ornaments (also in live spawns; here for review).
// Radii are larger than gameplay values — gallery is for review, not simulation.
const CREATURE_SPECS: CreatureSpec[] = [
  { label: 'ciliate',           row: 0, colF: 0.5, recipe: { body: 'grain', ornaments: ['cilia'], count: 18 },      radius: 0.12, tint: [0.60, 0.75, 1.00], variant: 'shallow', wobbleFreq: 1.5 },
  { label: 'flagellate',        row: 0, colF: 1.5, recipe: { body: 'grain', ornaments: ['flagella'] },              radius: 0.12, tint: [0.70, 0.80, 1.00], variant: 'shallow', wobbleFreq: 1.5 },
  { label: 'spore',             row: 0, colF: 2.5, recipe: { body: 'grain', ornaments: ['nucleusBig'] },            radius: 0.12, tint: [0.60, 0.70, 0.90], variant: 'deep',    wobbleFreq: 1.5 },
  { label: 'bristled',          row: 0, colF: 3.5, recipe: { body: 'grain', ornaments: ['tendrils'], count: 8 },    radius: 0.12, tint: [0.55, 0.65, 0.90], variant: 'deep',    wobbleFreq: 1.5 },
  { label: 'pseudopod',         row: 1, colF: 0,   recipe: { body: 'star', count: 6, length: 0.45 },                radius: 0.16, tint: [1.00, 0.35, 0.15], variant: 'shallow', wobbleFreq: 1.0 },
  { label: 'spiked',            row: 1, colF: 1,   recipe: { body: 'star', count: 12, length: 0.62 },               radius: 0.16, tint: [0.72, 0.24, 0.20], variant: 'deep',    wobbleFreq: 1.0 },
  { label: 'ciliate friend',    row: 1, colF: 2,   recipe: { body: 'blob', ornaments: ['cilia'], count: 14 },       radius: 0.12, tint: [0.34, 1.00, 0.62], variant: 'shallow', wobbleFreq: 1.5, eyes: 'chibi' },
  { label: 'flagellate friend', row: 1, colF: 3,   recipe: { body: 'blob', ornaments: ['flagella'] },               radius: 0.12, tint: [0.60, 1.00, 0.34], variant: 'shallow', wobbleFreq: 1.5, eyes: 'chibi' },
  { label: 'chain boss',        row: 1, colF: 4,   recipe: { body: 'blob' },                                        radius: 0.22, tint: [0.60, 0.80, 1.00], variant: 'shallow', wobbleFreq: 1.0, isChainSegment: true },
  // New bodies + ornaments (row 3).
  { label: 'rod',               row: 3, colF: 0,   recipe: { body: 'rod', length: 0.35 },                           radius: 0.14, tint: [0.65, 0.80, 1.00], variant: 'shallow', wobbleFreq: 1.2 },
  { label: 'spiral',            row: 3, colF: 1,   recipe: { body: 'spiral', count: 3 },                            radius: 0.14, tint: [0.60, 0.90, 0.80], variant: 'shallow', wobbleFreq: 1.2 },
  { label: 'plant cell',        row: 3, colF: 2,   recipe: { body: 'plantCell', ornaments: ['cellWall'] },          radius: 0.15, tint: [0.55, 0.85, 0.45], variant: 'shallow', wobbleFreq: 0.8 },
  { label: 'cell wall',         row: 3, colF: 3,   recipe: { body: 'blob', ornaments: ['cellWall'] },               radius: 0.13, tint: [0.85, 0.75, 0.55], variant: 'shallow', wobbleFreq: 1.2 },
  { label: 'dna strand',        row: 3, colF: 4,   recipe: { body: 'blob', ornaments: ['dnaStrand'] },              radius: 0.14, tint: [0.75, 0.60, 0.95], variant: 'shallow', wobbleFreq: 1.2 },
  { label: 'legs',              row: 3, colF: 5,   recipe: { body: 'blob', ornaments: ['legs'], count: 6 },         radius: 0.13, tint: [0.80, 0.70, 0.55], variant: 'shallow', wobbleFreq: 1.2 },
  { label: 'toadstool',         row: 3, colF: 6,   recipe: { body: 'mushroom', ornaments: ['gills'] },              radius: 0.16, tint: [0.78, 0.60, 0.45], variant: 'shallow', wobbleFreq: 0.9 },
  { label: 'hyphae',            row: 3, colF: 7,   recipe: { body: 'mushroom', ornaments: ['gills', 'hyphae'], count2: 9 }, radius: 0.16, tint: [0.72, 0.58, 0.42], variant: 'shallow', wobbleFreq: 0.9 },
]

// Row 2 — player at max stacks of each mutation, plus tier-2 baseline at right end.
// colF 0–4 = mutations (centered at 2.0); colF 5.5 = tier-2 with gap to separate it visually.
const MUTATION_ROW = 2
const PLAYER_PREVIEW_RADIUS = 0.18
const MUTATION_SPECS: MutationSpec[] = [
  { label: 'borrowed-teeth', colF: 0,   mutationFlags: 1,  mutationStacks: 4 },
  { label: 'bouquet',        colF: 1,   mutationFlags: 2,  mutationStacks: 4 << 4  },
  { label: 'nucleus-envy',   colF: 2,   mutationFlags: 4,  mutationStacks: 4 << 8  },
  { label: 'wet-socks',      colF: 3,   mutationFlags: 8,  mutationStacks: 4 << 12 },
  { label: 'static-cling',   colF: 4,   mutationFlags: 16, mutationStacks: 4 << 16 },
  { label: 'tier 2',         colF: 5.5, mutationFlags: 0,  mutationStacks: 0, tier: 1 },
]

// Row 4 — the 5 speciation forms, side by side for review. Rendered as
// player-body previews (own form, fully revealed) at a shared review tier the
// user cycles with T to inspect the tier 3→6 growth elaboration.
const SPECIES_ROW = 4
const SPECIES_SPECS: { form: PlayerForm; label: string; colF: number }[] =
  PLAYER_FORMS.map((form, i) => ({ form, label: FORM_DISPLAY[form], colF: i }))
const SPECIES_REVIEW_TIERS: Tier[] = [3, 4, 5, 6]

export interface CreatureGallery {
  isOpen(): boolean
  toggle(world: GameWorld, camera: Camera): void
  cycleTier(): void
  tick(elapsed: number, camera: Camera): void
  destroy(): void
}

export function createCreatureGallery(renderSystem: RenderSystem): CreatureGallery {
  let open = false
  let entities: Entity[] = []
  let savedCam = { x: 0, y: 0, zoom: 1 }
  // Species-row review tier — cycles 6→3→4→5→6 with T. Default fullest.
  let reviewTierIdx = SPECIES_REVIEW_TIERS.length - 1  // → tier 6

  const overlay = document.createElement('div')
  overlay.style.cssText =
    'position:fixed;inset:0;pointer-events:none;display:none;' +
    'font-family:Georgia,serif;font-size:12px;color:#3a2c20;'
  document.body.appendChild(overlay)

  const header = document.createElement('div')
  header.textContent = 'creature gallery  ·  ` to close  ·  T cycles species tier'
  header.style.cssText =
    'position:absolute;top:12px;left:50%;transform:translateX(-50%);' +
    'letter-spacing:0.08em;opacity:0.55;font-style:italic;'
  overlay.appendChild(header)

  const creatureLabelEls = CREATURE_SPECS.map(spec => {
    const el = document.createElement('span')
    el.textContent = spec.label
    el.style.cssText = 'position:absolute;transform:translateX(-50%);white-space:nowrap;'
    overlay.appendChild(el)
    return el
  })

  const mutationLabelEls = MUTATION_SPECS.map(spec => {
    const el = document.createElement('span')
    el.textContent = spec.label
    el.style.cssText = 'position:absolute;transform:translateX(-50%);white-space:nowrap;'
    overlay.appendChild(el)
    return el
  })

  const speciesLabelEls = SPECIES_SPECS.map(spec => {
    const el = document.createElement('span')
    el.textContent = spec.label
    el.style.cssText = 'position:absolute;transform:translateX(-50%);white-space:nowrap;font-weight:bold;'
    overlay.appendChild(el)
    return el
  })

  const tierBtn = document.createElement('button')
  tierBtn.style.cssText =
    'position:absolute;top:12px;right:16px;pointer-events:auto;cursor:pointer;' +
    'font-family:Georgia,serif;font-size:12px;color:#3a2c20;background:rgba(255,252,244,0.85);' +
    'border:1px solid #b9a88a;border-radius:4px;padding:4px 9px;'
  overlay.appendChild(tierBtn)
  const syncTierBtn = () => { tierBtn.textContent = `species tier ${SPECIES_REVIEW_TIERS[reviewTierIdx]}  ·  T` }
  syncTierBtn()

  const cycleTier = () => {
    reviewTierIdx = (reviewTierIdx + 1) % SPECIES_REVIEW_TIERS.length
    syncTierBtn()
  }
  tierBtn.addEventListener('click', cycleTier)

  return {
    isOpen() { return open },
    cycleTier,

    toggle(world, camera) {
      if (!open) {
        savedCam = { x: camera.x, y: camera.y, zoom: camera.zoom }

        entities = CREATURE_SPECS.map((spec, idx) => {
          const pos = worldPos(spec.row, spec.colF)
          const entity: Entity = spec.isChainSegment
            ? {
                position: { x: pos.x, y: pos.y },
                renderable: { shape: 'chainSegment', radius: spec.radius, tint: spec.tint },
                chainSegment: {
                  parent: { position: { x: pos.x, y: pos.y } } as Entity,
                  index: 0,
                  hueOffset: 0.3,
                  popTimer: 0,
                },
              }
            : {
                position: { x: pos.x, y: pos.y },
                wobble: { phase: 0, freq: spec.wobbleFreq, amp: 0.05 },
                renderable: { shape: 'microbe', radius: spec.radius, tint: spec.tint, variant: spec.variant },
                morphology: {
                  ...packRecipe(spec.recipe),
                  seed: (idx * 0.13) % 1.0,
                },
              }
          if (spec.eyes === 'angry') {
            attachEyes(entity, [
              { offset: { x: -0.36, y: 0.26 }, radius: 0.28 },
              { offset: { x:  0.36, y: 0.26 }, radius: 0.28 },
            ], { angry: 1 })
          } else if (spec.eyes === 'chibi') {
            attachEyes(entity, [
              { offset: { x: -0.34, y: 0.22 }, radius: 0.38 },
              { offset: { x:  0.34, y: 0.22 }, radius: 0.38 },
            ])
          }
          world.add(entity)
          return entity
        })

        // Auto-zoom to frame all content, derived from the actual rows/cols in
        // use so adding a row reframes safely.
        const maxR = 0.22  // chain boss segment gallery radius
        const creatureRows = CREATURE_SPECS.map(s => s.row)
        const minRow = Math.min(...creatureRows, MUTATION_ROW, SPECIES_ROW)
        const maxRow = Math.max(...creatureRows, MUTATION_ROW, SPECIES_ROW)
        // Widest horizontal offset from center col 2.0 across every item.
        const maxColOff = Math.max(
          ...CREATURE_SPECS.map(s => Math.abs(s.colF - 2.0)),
          ...MUTATION_SPECS.map(s => Math.abs(s.colF - 2.0)),
          ...SPECIES_SPECS.map(s => Math.abs(s.colF - 2.0)),
        )
        const halfSpan = maxColOff * COL_SPACING
        const worldW = 2 * halfSpan + 2 * maxR + 0.20
        const worldH = (maxRow - minRow) * ROW_SPACING + 2 * maxR + 0.20 + 0.14 // +0.14 for bottom labels
        const aspect = camera.viewportWidth / camera.viewportHeight
        // effectiveZoom = zoom / max(1, sqrt(aspect)) for REFERENCE_ASPECT=1.0
        const effZoom = Math.min(2 * aspect / worldW, 2 / worldH) * 0.88
        camera.zoom = effZoom * Math.max(1, Math.sqrt(aspect))
        camera.x = GALLERY_X
        // Center vertically on the mid row, nudged down for the bottom labels.
        const rowCenter = (minRow + maxRow) / 2
        camera.y = GALLERY_Y + (1.0 - rowCenter) * ROW_SPACING - 0.07

        syncTierBtn()
        overlay.style.display = 'block'
        open = true
      } else {
        for (const e of entities) {
          if (world.id(e) !== undefined) world.remove(e)
        }
        entities = []
        renderSystem.extraPlayers = []
        camera.x = savedCam.x
        camera.y = savedCam.y
        camera.zoom = savedCam.zoom
        overlay.style.display = 'none'
        open = false
      }
    },

    tick(elapsed, camera) {
      for (let i = 0; i < entities.length; i++) {
        const e = entities[i]
        if (e.wobble) e.wobble.phase = elapsed * e.wobble.freq

        const spec = CREATURE_SPECS[i]
        const cx = camera.worldToScreen(e.position.x, e.position.y)
        const bot = camera.worldToScreen(e.position.x, e.position.y - spec.radius)
        creatureLabelEls[i].style.left = `${cx.x}px`
        creatureLabelEls[i].style.top = `${bot.y + 14}px`
      }

      // Push mutation preview players into the render system's extra list
      const extras: ExtraPlayerSpec[] = []
      for (let i = 0; i < MUTATION_SPECS.length; i++) {
        const ms = MUTATION_SPECS[i]
        const pos = worldPos(MUTATION_ROW, ms.colF)
        extras.push({
          x: pos.x, y: pos.y,
          radius: PLAYER_PREVIEW_RADIUS,
          phase: elapsed * 1.0,
          tier: ms.tier,
          mutationFlags: ms.mutationFlags,
          mutationStacks: ms.mutationStacks,
        })

        const cx = camera.worldToScreen(pos.x, pos.y)
        const bot = camera.worldToScreen(pos.x, pos.y - PLAYER_PREVIEW_RADIUS)
        mutationLabelEls[i].style.left = `${cx.x}px`
        mutationLabelEls[i].style.top = `${bot.y + 14}px`
      }

      // Species row — each of the 5 forms, own body, at the review tier's growth.
      const growth = growthAt(SPECIES_REVIEW_TIERS[reviewTierIdx])
      for (let i = 0; i < SPECIES_SPECS.length; i++) {
        const sp = SPECIES_SPECS[i]
        const pos = worldPos(SPECIES_ROW, sp.colF)
        extras.push({
          x: pos.x, y: pos.y,
          radius: PLAYER_PREVIEW_RADIUS,
          phase: elapsed * 1.0,
          mutationFlags: 0,
          mutationStacks: 0,
          form: sp.form,
          growth,
        })

        const cx = camera.worldToScreen(pos.x, pos.y)
        const bot = camera.worldToScreen(pos.x, pos.y - PLAYER_PREVIEW_RADIUS)
        speciesLabelEls[i].style.left = `${cx.x}px`
        speciesLabelEls[i].style.top = `${bot.y + 14}px`
      }
      renderSystem.extraPlayers = extras
    },

    destroy() {
      overlay.remove()
    },
  }
}
